import React, { FC, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Divider, Modal, Text } from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { capitalize } from 'lodash';

import { getRideRequests, updateRideRequests } from '../../../api/services/Riders';
import { useTimezone } from '../../../hooks/useTimezone';
import { useToastStore } from '../../../shared/stores/toastStore';

interface PresentationProps {
  rideRequests: RideRequest[];
  handleUpdateRideRequests: (rideRequestIds: string, rideRequestAction: string) => void;
  rideRequestIds: string;
  isSubmitting: 'accepting' | 'rejecting' | '';
}

export const RideRequest: FC = () => {
  const [rideRequests, setRideRequests] = useState<RideRequest[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<'accepting' | 'rejecting' | ''>('');
  const { addToast } = useToastStore();
  const location = useLocation();
  const navigate = useNavigate();
  const urlSearchParams = new URLSearchParams(location.search);
  const openRequestsModal = urlSearchParams.get('openRequestsModal');
  const rider = urlSearchParams.get('rider') || '';

  const fetchRideRequests = async (currentRider) => {
    const response = await getRideRequests(currentRider);

    if (response?.type === 'success') {
      setRideRequests(response.data.rideRequests);
    } else {
      response.error.errors?.forEach((error) => addToast({ severity: 'error', message: error }));
    }
  };

  const handleUpdateRideRequests = async (rideRequestIds, rideRequestAction) => {
    setIsSubmitting(rideRequestAction);
    const response = await updateRideRequests(rider, rideRequestIds, rideRequestAction);

    if (response?.type === 'error') {
      response.error.errors?.forEach((error) => addToast({ severity: 'error', message: error }));
    } else {
      setRideRequests([]);
      navigate('/rides');
    }
    setIsSubmitting('');
  };

  useEffect(() => {
    if (openRequestsModal && rider) {
      fetchRideRequests(rider);
    }
  }, []);

  const rideRequestIds = useMemo(() => {
    return rideRequests.map((rideRequest) => rideRequest.id).toString();
  }, [rideRequests]);

  return (
    <Presentation
      rideRequests={rideRequests}
      isSubmitting={isSubmitting}
      rideRequestIds={rideRequestIds}
      handleUpdateRideRequests={handleUpdateRideRequests}
    />
  );
};

const Presentation: FC<PresentationProps> = ({
  rideRequests,
  handleUpdateRideRequests,
  rideRequestIds,
  isSubmitting,
}) => {
  const { formatInTimezone } = useTimezone();

  const getCTAText = (action: string) => {
    if (rideRequests.length > 1) {
      return isSubmitting === action ? capitalize(action + 'ing all...') : capitalize(action + ' all');
    }

    return isSubmitting === action ? capitalize(action + 'ing...') : capitalize(action);
  };

  return (
    <Modal open={rideRequests.length > 0} data-testid={'ride-requests-modal'}>
      <Modal.Title>Ride Requests for {rideRequests[0]?.riderName}</Modal.Title>
      <Modal.Content style={{ paddingTop: 0 }}>
        <Text fontWeight={300} mb={4} textColor="secondaryText" style={{ fontSize: '14px' }}>
          This will create all rides in the following ride series and will allow CareDrivers to claim these rides.
        </Text>

        {rideRequests.map((rideRequest, index) => (
          <React.Fragment key={rideRequest.id}>
            <Box display="flex" justifyContent="space-between">
              <Box flexDirection="column">
                <Text>
                  {formatInTimezone(rideRequest.anchorTime, 'hh:mm aa')}{' '}
                  {rideRequest.timeAnchor === 'drop_off' ? 'dropoff' : 'pickup'}
                </Text>
                <Text fontWeight={300} textColor="secondaryText">
                  {rideRequest.address}
                </Text>
              </Box>
              <Box flexDirection="column" style={{ textAlign: 'end' }}>
                {rideRequest.daysOfWeek?.length && (
                  <Text>
                    {rideRequest.daysOfWeek.map(
                      (dow, dayIndex, array) => `${getFormattedDOW(dow)} ${dayIndex < array.length - 1 ? ', ' : ''}`
                    )}
                  </Text>
                )}
                <Text>Start date: {rideRequest.startDate}</Text>
                <Text fontWeight={300} textColor="secondaryText">
                  <i>Requested: {formatInTimezone(rideRequest.anchorTime, 'MM/dd/YYY')}</i>
                </Text>
              </Box>
            </Box>
            {index < rideRequests.length - 1 && <Divider my={4} />}
          </React.Fragment>
        ))}
      </Modal.Content>
      <Modal.Actions>
        <Button
          color="secondary"
          variant="contained"
          disabled={!!isSubmitting}
          onClick={() => handleUpdateRideRequests(rideRequestIds, 'reject')}
        >
          {getCTAText('reject')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!!isSubmitting}
          onClick={() => handleUpdateRideRequests(rideRequestIds, 'accept')}
        >
          {getCTAText('accept')}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const getFormattedDOW = (weekDay: string) => {
  switch (weekDay) {
    case 'monday':
      return 'M';
    case 'tuesday':
      return 'Tu';
    case 'wednesday':
      return 'W';
    case 'thursday':
      return 'Th';
    case 'friday':
      return 'F';
    case 'saturday':
      return 'Sa';
    case 'sunday':
      return 'Su';
    default:
      return '';
  }
};
